import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, Mail, CheckCircle, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState('email'); // email, otp, newPassword, success
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [resetToken, setResetToken] = useState('');

  const handleRequestReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      toast.success('Password reset code sent to your email');
      setStep('otp');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send reset code. Please check your email.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/verify-reset-code`, { email, code: otp });
      setResetToken(res.data.reset_token);
      toast.success('Code verified successfully');
      setStep('newPassword');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid or expired code');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { 
        email, 
        reset_token: resetToken, 
        new_password: newPassword 
      });
      toast.success('Password reset successfully!');
      setStep('success');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center bg-[#F8FAFC] py-16" data-testid="forgot-password-page">
      <div className="w-full max-w-md px-4">
        <div className="text-center mb-8">
          <img
            src="https://customer-assets.emergentagent.com/job_cbeam-portal/artifacts/2eoy3xqe_IMCAC%20%286%29.png"
            alt="IMCAC Logo"
            className="h-14 w-14 mx-auto mb-3 rounded-sm"
          />
          <h1 className="text-2xl font-bold text-[#1B2A4A] font-heading">Reset Password</h1>
          <p className="text-sm text-slate-500 mt-1">
            {step === 'email' && 'Enter your email to receive a reset code'}
            {step === 'otp' && 'Enter the code sent to your email'}
            {step === 'newPassword' && 'Create your new password'}
            {step === 'success' && 'Your password has been reset'}
          </p>
        </div>

        <Card className="imcac-card">
          <CardContent className="p-6">
            {step === 'email' && (
              <form onSubmit={handleRequestReset} className="space-y-4">
                <div>
                  <Label htmlFor="reset-email" className="text-sm font-medium text-slate-700">Email Address</Label>
                  <Input
                    id="reset-email"
                    type="email"
                    placeholder="your.email@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="rounded-sm mt-1"
                    data-testid="reset-email-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm" data-testid="send-code-btn">
                  <Mail className="h-4 w-4 mr-2" />
                  {loading ? 'Sending...' : 'Send Reset Code'}
                </Button>
              </form>
            )}

            {step === 'otp' && (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 mb-4">
                  <p className="text-sm text-blue-700">A 6-digit code has been sent to <strong>{email}</strong></p>
                </div>
                <div>
                  <Label htmlFor="otp-code" className="text-sm font-medium text-slate-700">Verification Code</Label>
                  <Input
                    id="otp-code"
                    type="text"
                    placeholder="Enter 6-digit code"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value)}
                    required
                    maxLength={6}
                    className="rounded-sm mt-1 text-center text-2xl tracking-widest"
                    data-testid="otp-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm" data-testid="verify-code-btn">
                  {loading ? 'Verifying...' : 'Verify Code'}
                </Button>
                <button 
                  type="button" 
                  onClick={() => setStep('email')} 
                  className="w-full text-sm text-slate-500 hover:text-[#C5A572]"
                >
                  Didn't receive code? Try again
                </button>
              </form>
            )}

            {step === 'newPassword' && (
              <form onSubmit={handleResetPassword} className="space-y-4">
                <div>
                  <Label htmlFor="new-password" className="text-sm font-medium text-slate-700">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    placeholder="Enter new password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="rounded-sm mt-1"
                    data-testid="new-password-input"
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password" className="text-sm font-medium text-slate-700">Confirm Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    placeholder="Confirm new password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="rounded-sm mt-1"
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm" data-testid="reset-password-btn">
                  <KeyRound className="h-4 w-4 mr-2" />
                  {loading ? 'Resetting...' : 'Reset Password'}
                </Button>
              </form>
            )}

            {step === 'success' && (
              <div className="text-center py-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-green-600" />
                </div>
                <h3 className="text-lg font-semibold text-[#1B2A4A] mb-2">Password Reset Successful!</h3>
                <p className="text-sm text-slate-500 mb-6">You can now sign in with your new password.</p>
                <Button onClick={() => navigate('/login')} className="w-full bg-[#1B2A4A] hover:bg-[#152039] text-white rounded-sm" data-testid="back-to-login-btn">
                  Back to Sign In
                </Button>
              </div>
            )}

            {step !== 'success' && (
              <div className="mt-6 pt-4 border-t border-slate-100">
                <button 
                  onClick={() => navigate('/login')} 
                  className="flex items-center justify-center w-full text-sm text-slate-500 hover:text-[#C5A572]"
                  data-testid="back-link"
                >
                  <ArrowLeft className="h-4 w-4 mr-1" /> Back to Sign In
                </button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
